import { createElement as _createElement } from "react";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Box, Chip, CircularProgress, Typography, useTheme, alpha, Tooltip } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import * as React from 'react';
import { HFilledTextField } from '../TextField';
import { normalizeAutocompleteInputValue, wrapAutocompleteInputProps } from '../TextField/text-input-normalize';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
const HCheckboxAutoComplete = React.forwardRef(({ id, options, size = 'medium', disableCloseOnSelect = true, label, placeholder, disabled = false, error, loading = false, bg = 'light', maxChips = 3, onChange, value: externalValue, getOptionKey, checkboxMode = true, endAdornment, endAdornmentIcon, showDesignation = false, showLocation = false, showName = false, showSelectAll = false, selectAllLabel = 'Select All', isSortable = false, tagsPosition = 'inside', treatDisabledAsMandatory = true, disablingOptionValue, hidePlaceholderWhenValue = false, selectedChipSx, showCountTextWhenMultiple = false, selectedChipVariant = 'filled', selectedChipColor = 'default', trimWhitespace = true, onInputChange, ...autocompleteProps }, ref) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const handleInputChange = React.useCallback((event, value, reason) => {
        onInputChange?.(event, trimWhitespace ? normalizeAutocompleteInputValue(value) : value, reason);
    }, [onInputChange, trimWhitespace]);
    // Add "Select All" option at the beginning if showSelectAll is true
    const optionsWithSelectAll = React.useMemo(() => {
        if (!showSelectAll)
            return options;
        // Check if "Select All" option already exists
        const hasSelectAll = options.some((opt) => opt.value === 'all');
        if (hasSelectAll)
            return options;
        return [{ label: selectAllLabel, value: 'all' }, ...options];
    }, [options, showSelectAll, selectAllLabel]);
    const otherOptions = optionsWithSelectAll.filter((opt) => opt.value !== 'all'); // other options except all
    const selectAllOption = optionsWithSelectAll.find((opt) => opt.value === 'all');
    const mandatoryOptions = React.useMemo(() => (treatDisabledAsMandatory ? otherOptions.filter((opt) => !!opt.isDisabled) : []), [otherOptions, treatDisabledAsMandatory]);
    // Helper function to normalize external value and add "Select All" if all items are selected
    const normalizeValue = React.useCallback((value) => {
        if (!value || !Array.isArray(value) || value.length === 0)
            return [];
        // Filter out any "all" option from external value
        let filteredValue = value.filter((opt) => opt.value !== 'all');
        // Ensure mandatory (disabled) options are always present
        const missingMandatory = mandatoryOptions.filter((m) => !filteredValue.some((v) => v.value === m.value));
        if (missingMandatory.length > 0) {
            filteredValue = [...filteredValue, ...missingMandatory];
        }
        // Calculate other options length (excluding "all")
        const otherOptionsCount = optionsWithSelectAll.filter((opt) => opt.value !== 'all').length;
        // If all options are selected and showSelectAll is true, add "Select All" option
        if (showSelectAll &&
            selectAllOption &&
            filteredValue.length > 0 &&
            filteredValue.length === otherOptionsCount) {
            return [selectAllOption, ...filteredValue];
        }
        return filteredValue;
    }, [showSelectAll, selectAllOption, optionsWithSelectAll, mandatoryOptions]);
    const [selectedOptions, setSelectedOptions] = React.useState(() => normalizeValue(Array.isArray(externalValue) ? externalValue : []) // ← ADD Array.isArray guard
    );
    const optionsByValue = React.useMemo(() => new Map(optionsWithSelectAll.map((opt) => [String(opt.value), opt])), [optionsWithSelectAll]);
    const hydrateSelectedWithLatestOptions = React.useCallback((value) => value.map((opt) => {
        const latest = optionsByValue.get(String(opt.value));
        // Keep selected identity by value, but refresh display metadata from latest options.
        return latest ? { ...opt, ...latest, value: opt.value } : opt;
    }), [optionsByValue]);
    const areSelectedOptionsEqual = React.useCallback((a, b) => {
        if (a.length !== b.length)
            return false;
        return a.every((opt, idx) => {
            const other = b[idx];
            return (opt.value === other?.value &&
                opt.label === other?.label &&
                opt.iconUrl === other?.iconUrl &&
                opt.designation === other?.designation &&
                opt.email === other?.email &&
                opt.companyName === other?.companyName &&
                opt.isDisabled === other?.isDisabled &&
                opt.isPrimary === other?.isPrimary &&
                opt.primaryBadge === other?.primaryBadge &&
                opt.primaryLabel === other?.primaryLabel);
        });
    }, []);
    // Check if the disabling option is selected
    const isDisablingOptionSelected = React.useMemo(() => {
        if (!disablingOptionValue)
            return false;
        return selectedOptions?.some((opt) => opt.value === disablingOptionValue) ?? false;
    }, [selectedOptions, disablingOptionValue]);
    // Sync with external value prop (guard to avoid infinite re-render loops)
    React.useEffect(() => {
        if (externalValue === undefined || !Array.isArray(externalValue))
            return; // ← ADD Array.isArray check
        const normalized = normalizeValue(externalValue);
        const hydrated = hydrateSelectedWithLatestOptions(normalized);
        if (!areSelectedOptionsEqual(hydrated, selectedOptions)) {
            setSelectedOptions(hydrated);
        }
    }, [
        externalValue,
        normalizeValue,
        hydrateSelectedWithLatestOptions,
        areSelectedOptionsEqual,
        selectedOptions
    ]);
    // Sort options: selected options at top (when isSortable is true), but keep "Select All" at the very top
    const sortedOptions = React.useMemo(() => {
        if (!isSortable) {
            return optionsWithSelectAll;
        }
        const selectedValues = new Set(selectedOptions.map((opt) => opt.value));
        const selected = optionsWithSelectAll.filter((opt) => opt.value !== 'all' && selectedValues.has(opt.value));
        const unselected = optionsWithSelectAll.filter((opt) => opt.value !== 'all' && !selectedValues.has(opt.value));
        // Keep "Select All" at the top, then selected options, then unselected
        return selectAllOption
            ? [selectAllOption, ...selected, ...unselected]
            : [...selected, ...unselected];
    }, [optionsWithSelectAll, selectedOptions, isSortable, selectAllOption]);
    // Helper function to filter out "all" option and only send actual option IDs
    const getFilteredValue = React.useCallback((value) => {
        return value.filter((opt) => opt.value !== 'all');
    }, []);
    const handleChange = (_event, newValue, reason) => {
        // ✅ Mutual exclusivity logic for disablingOptionValue
        if (disablingOptionValue) {
            const hasDisabling = newValue.some((opt) => opt.value === disablingOptionValue);
            if (hasDisabling) {
                const onlyDisabling = newValue.find((opt) => opt.value === disablingOptionValue);
                if (onlyDisabling) {
                    setSelectedOptions([onlyDisabling]);
                    onChange?.([onlyDisabling]);
                    return;
                }
            }
            else {
                // If selecting other options, remove disabling option
                newValue = newValue.filter((opt) => opt.value !== disablingOptionValue);
            }
        }
        const isAllInNewValue = newValue.some((opt) => opt.value === 'all');
        const isAllInSelected = selectedOptions.some((opt) => opt.value === 'all');
        const otherValues = newValue.filter((opt) => opt.value !== 'all');
        // Case 1: User selects "all" (when it wasn't selected before) - select all options
        if (isAllInNewValue && !isAllInSelected) {
            const allOptionsSelected = selectAllOption
                ? [selectAllOption, ...otherOptions]
                : [...otherOptions];
            setSelectedOptions(allOptionsSelected);
            // Send only actual options (not "all") - all option IDs
            onChange?.(otherOptions);
            return;
        }
        // Case 2: User clicks on already checked "Select All" - deselect all items
        if (isAllInSelected && !isAllInNewValue) {
            // Keep mandatory (disabled) options selected
            const keep = [...mandatoryOptions];
            setSelectedOptions(keep);
            onChange?.(keep);
            return;
        }
        // Case 3: If "all" is selected and user unselects any other option then unselect "all" as well
        if (isAllInSelected && isAllInNewValue && otherOptions.length !== otherValues.length) {
            // Ensure mandatory options remain selected
            const updatedValue = [
                ...otherValues,
                ...mandatoryOptions.filter((m) => !otherValues.some((v) => v.value === m.value))
            ];
            setSelectedOptions(updatedValue);
            // Send only actual options (not "all")
            onChange?.(updatedValue);
            return;
        }
        // Case 4: "all" is not selected and user selects an option which makes all options selected - auto-select "all"
        if (!isAllInSelected &&
            !isAllInNewValue &&
            otherOptions.length === otherValues.length &&
            showSelectAll &&
            selectAllOption) {
            const allOptionsSelected = [selectAllOption, ...otherValues];
            setSelectedOptions(allOptionsSelected);
            // Send only actual options (not "all") - all option IDs
            onChange?.(otherOptions);
            return;
        }
        // Default: Update selected options and check if all are selected to auto-select "Select All"
        let finalValue = newValue;
        // Ensure mandatory options remain selected in all cases
        finalValue = [
            ...finalValue.filter((opt) => opt.value !== 'all'),
            ...mandatoryOptions.filter((m) => !finalValue.some((v) => v.value === m.value))
        ];
        if (!isAllInNewValue &&
            otherOptions.length === otherValues.length &&
            showSelectAll &&
            selectAllOption) {
            // All options are selected, add "Select All" automatically
            finalValue = [selectAllOption, ...otherOptions];
        }
        setSelectedOptions(finalValue);
        // Always send only actual options (not "all")
        onChange?.(getFilteredValue(finalValue));
    };
    const icon = _jsx(CheckBoxOutlineBlankIcon, { fontSize: 'small' });
    const checkedIcon = _jsx(CheckBoxIcon, { fontSize: 'small' });
    // Custom filter function to search by label, email, designation, and companyName
    const filterOptions = (options, { inputValue: rawInputValue }) => {
        const inputValue = trimWhitespace
            ? normalizeAutocompleteInputValue(rawInputValue)
            : rawInputValue;
        if (!inputValue)
            return options;
        const searchLower = inputValue.toLowerCase();
        return options.filter((option) => {
            const labelMatch = option.label?.toLowerCase().includes(searchLower);
            const emailMatch = option.email?.toLowerCase().includes(searchLower);
            const designationMatch = option.designation?.toLowerCase().includes(searchLower);
            const companyMatch = option.companyName?.toLowerCase().includes(searchLower);
            const nameMatch = showName && option.name?.toLowerCase().includes(searchLower);
            return labelMatch || emailMatch || designationMatch || companyMatch || nameMatch;
        });
    };
    // If not checkboxMode, render simple Autocomplete
    if (!checkboxMode) {
        return (_jsx(Autocomplete, { multiple: true, fullWidth: true, size: size, id: `${id}-simple-multiselect`, options: options, disabled: disabled, loading: loading, disableCloseOnSelect: disableCloseOnSelect, openOnFocus: false, filterOptions: (opts, state) => {
                const inputValue = trimWhitespace
                    ? normalizeAutocompleteInputValue(state.inputValue)
                    : state.inputValue;
                if (!inputValue)
                    return [];
                return opts.filter((opt) => opt.label.toLowerCase().includes(inputValue.toLowerCase()));
            }, onInputChange: handleInputChange, popupIcon: endAdornmentIcon ? (_jsx("span", { style: { transform: 'none', transition: 'none' }, children: endAdornmentIcon })) : null, onChange: (_event, newValue, reason) => {
                setSelectedOptions(newValue);
                onChange(newValue);
            }, isOptionEqualToValue: (o, v) => o.value === v.value, getOptionDisabled: (option) => {
                if (isDisablingOptionSelected &&
                    option.value !== disablingOptionValue &&
                    !selectedOptions.some((o) => o.value === option.value)) {
                    return true;
                }
                return !!option.isDisabled;
            }, ...(getOptionKey && { getOptionKey }), groupBy: autocompleteProps.groupBy, renderGroup: autocompleteProps.renderGroup, sx: {
                '& .MuiFilledInput-root .MuiAutocomplete-endAdornment': {
                    right: '3px',
                    '& .MuiAutocomplete-popupIndicator': { transform: 'none' }
                }
            }, slotProps: {
                paper: {
                    sx: {
                        backgroundColor: '#FFFFFFEB',
                        backdropFilter: 'blur(40px)',
                        color: (theme) => theme.palette.text.primary,
                        boxShadow: '-20px 20px 40px -4px #919EAB3D, 0px 0px 2px 0px #919EAB3D'
                    }
                }
            }, renderTags: (value, getTagProps) => {
                const chips = value.slice(0, maxChips).map((option, index) => (_createElement(Chip, { variant: 'outlined', label: option.label, size: 'small', sx: {
                        bgcolor: (theme) => (bg === 'blue' ? 'transparent' : theme.palette.text.primary),
                        color: (theme) => theme.palette.common.white
                    }, ...getTagProps({ index }), key: index })));
                if (value.length > maxChips) {
                    chips.push(_jsx(Chip, { label: `+${value.length - maxChips}`, size: 'small', sx: {
                            bgcolor: (theme) => (bg !== 'blue' ? theme.palette.text.primary : ''),
                            color: (theme) => theme.palette.common.white
                        } }, 'more'));
                }
                return chips;
            }, renderInput: (params) => (_jsx(HFilledTextField, { ...params, trimWhitespace: trimWhitespace, inputProps: wrapAutocompleteInputProps(params.inputProps, trimWhitespace), label: label, inputRef: ref, placeholder: placeholder, disabled: disabled, error: !!error, helperText: error, bg: bg, slotProps: {
                    input: {
                        ...params.InputProps,
                        endAdornment: (_jsxs(_Fragment, { children: [loading && _jsx(CircularProgress, { color: 'inherit', size: 20 }), params.InputProps.endAdornment] }))
                    }
                } })), ...autocompleteProps }));
    }
    // Checkbox mode (default)
    const renderTagsBelow = tagsPosition === 'below';
    const hasRealSelection = selectedOptions.some((opt) => opt.value !== 'all');
    return (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 1, width: '100%', children: [_jsx(Autocomplete, { multiple: true, fullWidth: true, size: size, id: `${id}-checkbox-multiselect`, options: sortedOptions, value: selectedOptions, disabled: disabled, loading: loading, disableCloseOnSelect: disableCloseOnSelect, filterOptions: filterOptions, onInputChange: handleInputChange, onChange: (_event, newValue, reason) => handleChange(_event, newValue, reason), isOptionEqualToValue: (o, v) => o.value === v.value, getOptionDisabled: (option) => {
                    // If disabling option is selected, disable all other options
                    if (isDisablingOptionSelected && option.value !== disablingOptionValue) {
                        return true;
                    }
                    return !!option.isDisabled;
                }, ...(getOptionKey && { getOptionKey }), groupBy: autocompleteProps.groupBy, renderGroup: autocompleteProps.renderGroup, popupIcon: _jsx(Icon, { icon: 'eva:arrow-ios-downward-fill', color: bg === 'blue' ? theme.palette.common.white : theme.palette.grey[700] }), sx: {
                    '& .MuiFilledInput-root .MuiAutocomplete-endAdornment': {
                        right: '3px'
                    }
                }, slotProps: {
                    paper: {
                        sx: {
                            backgroundColor: '#FFFFFFEB',
                            backdropFilter: 'blur(40px)',
                            color: (theme) => theme.palette.text.primary,
                            boxShadow: '-20px 20px 40px -4px #919EAB3D, 0px 0px 2px 0px #919EAB3D',
                            '& .MuiAutocomplete-noOptions': {
                                color: (theme) => theme.palette.text.primary
                            }
                        }
                    }
                }, renderTags: (value, getTagProps) => {
                    if (renderTagsBelow) {
                        const filteredValue = value.filter((opt) => opt.value !== 'all');
                        if (filteredValue.length === 0)
                            return null;
                        return (_jsxs(Typography, { variant: 'body1', sx: { ml: 1, color: 'text.primary' }, children: [filteredValue.length, " selected"] }));
                    }
                    // Filter out "Select All" option from chips display
                    const filteredValue = value.filter((opt) => opt.value !== 'all');
                    if (filteredValue.length === 0)
                        return null;
                    if (showCountTextWhenMultiple && filteredValue.length > 1) {
                        return (_jsx(Typography, { variant: 'body2', sx: { ml: 1, color: theme.palette.text.primary }, children: t('common.countSelected', { count: filteredValue.length }) }));
                    }
                    const visible = filteredValue.slice(0, maxChips);
                    const extraCount = filteredValue.length - visible.length;
                    return [
                        ...visible.map((option) => {
                            // Find the original index in the value array
                            const originalIndex = value.findIndex((opt) => opt.value === option.value);
                            const tagProps = getTagProps({ index: originalIndex });
                            // Prevent deleting chips for disabled options (use options config as source of truth)
                            const sourceOption = optionsWithSelectAll.find((opt) => opt.value === option.value);
                            const isDisabledChip = !!sourceOption?.isDisabled;
                            const { onDelete, ...restTagProps } = tagProps;
                            return (_createElement(Chip, { ...(isDisabledChip ? restTagProps : tagProps), key: option.value, label: option.label, variant: selectedChipVariant, color: selectedChipColor, avatar: option.iconUrl ? (_jsx(Avatar, { src: option.iconUrl, sx: { width: 18, height: 18 }, variant: 'rounded' })) : undefined, ...(isDisabledChip ? { onDelete: undefined, deleteIcon: undefined } : {}), size: 'small', sx: {
                                    borderRadius: '12px',
                                    height: 24,
                                    ...(selectedChipVariant === 'filled' && selectedChipColor === 'default'
                                        ? {
                                            bgcolor: '#0B0F19',
                                            color: theme.palette.common.white
                                        }
                                        : {}),
                                    ...selectedChipSx
                                } }));
                        }),
                        ...(extraCount > 0
                            ? [
                                _jsx(Tooltip, { title: _jsx(Box, { sx: {
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 0.5,
                                            maxHeight: 200,
                                            overflowY: 'auto'
                                        }, children: filteredValue.slice(maxChips).map((option) => (_jsx(Typography, { variant: 'caption', children: option.label }, option.value))) }), arrow: true, placement: 'top', children: _jsx(Chip, { label: `+${extraCount}`, size: 'small', sx: {
                                            bgcolor: '#0c0f15',
                                            color: theme.palette.common.white,
                                            borderRadius: '12px',
                                            height: 24
                                        } }, 'more-count') }, 'more-count-tooltip')
                            ]
                            : [])
                    ];
                }, renderOption: (props, option, { selected }) => {
                    const { key, ...optionProps } = props;
                    const isDisabled = !!option.isDisabled ||
                        (isDisablingOptionSelected && option.value !== disablingOptionValue);
                    return (_jsx("li", { ...optionProps, style: {
                            ...optionProps.style,
                            opacity: isDisabled ? 0.5 : 1,
                            color: isDisabled ? '#999' : 'inherit',
                            cursor: isDisabled ? 'not-allowed' : 'pointer'
                        }, children: _jsxs(Box, { sx: {
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%'
                            }, children: [_jsx(Checkbox, { icon: icon, checkedIcon: checkedIcon, style: {
                                        marginRight: 8,
                                        color: isDisabled ? '#ccc' : 'inherit',
                                        alignSelf: showDesignation ? 'flex-start' : 'center',
                                        marginTop: showDesignation ? '4px' : 0
                                    }, checked: selected, disabled: isDisabled }), _jsxs(Box, { sx: {
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flex: 1
                                    }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [option.iconUrl ? (_jsx("img", { src: option.iconUrl, alt: option.label, width: 18, height: 12, style: { borderRadius: 2 } })) : null, _jsx(Typography, { variant: 'body2', component: 'span', sx: {
                                                        color: isDisabled ? '#999' : 'inherit',
                                                        lineHeight: 1.5
                                                    }, children: option.isPrimary && option.primaryBadge ? (_jsxs(_Fragment, { children: [option.label.replace(/\s*\(.*?\)\s*$/i, ''), ' ', _jsxs(Typography, { component: 'span', variant: 'body2', sx: {
                                                                    fontWeight: 700,
                                                                    color: isDisabled ? '#999' : 'inherit'
                                                                }, children: ["(", option.primaryLabel || 'Primary', ")"] })] })) : (option.label) })] }), (option.email ||
                                            option.designation ||
                                            option.companyName ||
                                            (showLocation && option.location) ||
                                            (showName && (option.name || option.meta?.length))) && (_jsxs(Box, { sx: { mt: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }, children: [option.email && (_jsx(Typography, { variant: 'caption', sx: {
                                                        color: (theme) => isDisabled
                                                            ? theme.palette.text.disabled
                                                            : bg === 'grey8'
                                                                ? theme.palette.text.secondary
                                                                : theme.palette.common.white,
                                                        fontSize: '0.75rem',
                                                        lineHeight: 1.2
                                                    }, children: option.email })), (option.designation || option.companyName) && (_jsx(Typography, { variant: 'caption', sx: {
                                                        color: (theme) => isDisabled
                                                            ? theme.palette.text.disabled
                                                            : bg === 'grey8'
                                                                ? theme.palette.text.secondary
                                                                : theme.palette.common.white,
                                                        fontSize: '0.75rem',
                                                        lineHeight: 1.2
                                                    }, children: [option.designation, option.companyName].filter(Boolean).join(' • ') })), showLocation && option.location && (_jsx(Typography, { variant: 'caption', sx: {
                                                        color: (theme) => isDisabled
                                                            ? theme.palette.text.disabled
                                                            : bg === 'grey8'
                                                                ? theme.palette.text.secondary
                                                                : theme.palette.common.white,
                                                        fontSize: '0.75rem',
                                                        lineHeight: 1.2
                                                    }, children: option.location })), showName && !!option.meta?.length && (_jsx(Box, { sx: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: '12px', rowGap: '2px' }, children: option.meta.map((item) => (_jsxs(Box, { sx: {
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                            color: (theme) => isDisabled
                                                                ? theme.palette.text.disabled
                                                                : bg === 'grey8'
                                                                    ? theme.palette.text.secondary
                                                                    : theme.palette.common.white
                                                        }, children: [_jsx(Icon, { icon: item.icon, fontSize: 14 }), _jsx(Typography, { variant: 'caption', sx: { color: 'inherit', fontSize: '0.75rem', lineHeight: 1.2 }, children: item.text })] }, item.icon))) })), showName && option.name && !option.meta?.length && (_jsx(Typography, { variant: 'caption', sx: {
                                                        color: (theme) => isDisabled
                                                            ? theme.palette.text.disabled
                                                            : bg === 'grey8'
                                                                ? theme.palette.text.secondary
                                                                : theme.palette.common.white,
                                                        fontSize: '0.75rem',
                                                        lineHeight: 1.2
                                                    }, children: option.name }))] })), showDesignation &&
                                            !option.email &&
                                            !option.designation &&
                                            !option.companyName && (_jsx(Typography, { variant: 'caption', component: 'span', sx: {
                                                color: (theme) => isDisabled
                                                    ? theme.palette.text.disabled
                                                    : bg === 'grey8'
                                                        ? theme.palette.text.secondary
                                                        : theme.palette.common.white,
                                                fontSize: '0.75rem',
                                                lineHeight: 1.2,
                                                mt: '2px'
                                            }, children: option.companyName && option.designation ? (_jsxs(_Fragment, { children: [option.companyName, ' ', _jsx(Box, { component: 'span', sx: {
                                                            color: (theme) => theme.palette.primary.main,
                                                            mx: 0.5
                                                        }, children: "\u2022" }), ' ', "Designation: ", option.designation] })) : option.companyName ? (option.companyName) : option.designation ? (`Designation: ${option.designation}`) : ('') })), option.description && (_jsx(Tooltip, { title: option.description, children: _jsx(Typography, { variant: 'caption', sx: {
                                                    color: (theme) => isDisabled
                                                        ? theme.palette.text.disabled
                                                        : bg === 'grey8'
                                                            ? theme.palette.text.secondary
                                                            : theme.palette.common.white,
                                                    fontSize: '0.7rem',
                                                    lineHeight: 1.3,
                                                    mt: '4px',
                                                    cursor: 'help',
                                                    display: 'block'
                                                }, children: option.description }) }))] })] }) }, key));
                }, renderInput: (params) => (_jsx(HFilledTextField, { ...params, trimWhitespace: trimWhitespace, inputProps: wrapAutocompleteInputProps(params.inputProps, trimWhitespace), label: label, inputRef: ref, placeholder: !!placeholder && hasRealSelection && (hidePlaceholderWhenValue || renderTagsBelow)
                        ? ''
                        : placeholder, disabled: disabled, error: !!error, helperText: error, bg: bg, slotProps: {
                        input: {
                            ...params.InputProps,
                            endAdornment: (_jsxs(_Fragment, { children: [loading && _jsx(CircularProgress, { color: 'inherit', size: 20 }), params.InputProps.endAdornment] }))
                        }
                    } })), ...autocompleteProps }), renderTagsBelow && selectedOptions.length > 0 && (_jsx(Box, { display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, children: selectedOptions
                    .filter((opt) => opt.value !== 'all')
                    .map((option) => {
                    const isDisabledChip = !!optionsWithSelectAll.find((o) => o.value === option.value)
                        ?.isDisabled;
                    return (_jsx(Chip, { label: option.label, onDelete: isDisabledChip
                            ? undefined
                            : () => {
                                const newValue = selectedOptions.filter((o) => o.value !== option.value);
                                handleChange(null, newValue, 'removeOption');
                            }, avatar: option.iconUrl ? (_jsx(Avatar, { src: option.iconUrl, sx: { width: 18, height: 18 }, variant: 'rounded' })) : undefined, size: 'medium', sx: {
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
                            color: (theme) => theme.palette.primary.dark,
                            borderRadius: '8px',
                            fontWeight: 600,
                            '& .MuiChip-deleteIcon': {
                                color: (theme) => theme.palette.primary.dark,
                                '&:hover': {
                                    color: (theme) => theme.palette.primary.dark
                                }
                            }
                        } }, option.value));
                }) }))] }));
});
export default HCheckboxAutoComplete;
